import { useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { FaHeart, FaStar } from "react-icons/fa";
import ProductCard from "../components/ProductCard";
import { getProduct, getALLproducts } from "../services/productService";
import { useInquiry } from "../context/InquiryContext";
import ImageWithSkeleton from "../components/ImageWithSkeleton";

const IMG_BASE =
  import.meta.env.VITE_API_URL ?? "http://localhost:8000/storage";

const mapApiProduct = (p) => {
  const price = 20 + (p.name.charCodeAt(0) % 50);
  const oldPrice = price + 10;
  const sortedImages = p.images
    ? [...p.images].sort((a, b) => (b.is_primary ?? 0) - (a.is_primary ?? 0))
    : [];
  const primary = sortedImages[0];
  const img1 = primary
    ? primary.image.startsWith("http")
      ? primary.image
      : `${IMG_BASE}/${primary.image}`
    : "/img/product/placeholder.jpg";
  const second = sortedImages.length > 1 ? sortedImages[1] : primary;
  const img2 = second
    ? second.image.startsWith("http")
      ? second.image
      : `${IMG_BASE}/${second.image}`
    : img1;
  const badge =
    p.name.charCodeAt(1) % 3 === 0
      ? ["Sale"]
      : p.name.charCodeAt(1) % 3 === 1
        ? ["Sale", "New"]
        : [];
  return {
    id: p.product_id,
    product_id: p.product_id,
    name: p.name,
    category: p.category?.name || "",
    category_id: p.category_id,
    price,
    oldPrice,
    img1,
    img2,
    badge,
    description: p.description,
    additional_information: p.additional_information,
    status: p.status,
    images: sortedImages,
  };
};

export default function ProductDetailsPage() {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(false);
  const [qty, setQty] = useState(1);
  const [mainImg, setMainImg] = useState(null);
  const [activeTab, setActiveTab] = useState("additional");
  const [relatedProducts, setRelatedProducts] = useState([]);

  const { openInquiry } = useInquiry();
  console.log({ product });

  useEffect(() => {
    setLoading(true);
    const loadDetails = async () => {
      let currentProd = null;
      try {
        if (id) {
          const res = await getProduct(id);
          currentProd = res.data?.data ?? null;
        } else {
          const res = await getALLproducts();
          const list = res.data?.data || res.data || [];
          if (list.length > 0) currentProd = list[0];
        }
      } catch (err) {
        console.error("Failed to load product details:", err);
      }

      if (currentProd && currentProd.images) {
        currentProd.images = [...currentProd.images].sort(
          (a, b) => (b.is_primary ?? 0) - (a.is_primary ?? 0),
        );
      }

      setProduct(currentProd);

      if (currentProd) {
        const primary = currentProd.images?.[0];
        setMainImg(
          primary
            ? primary.image.startsWith("http")
              ? primary.image
              : `${IMG_BASE}/${primary.image}`
            : null,
        );

        try {
          const res = await getALLproducts();
          const list = res.data?.data || res.data || [];
          const mapped = list.map(mapApiProduct);
          const filtered = mapped.filter(
            (p) =>
              p.category_id === currentProd.category_id &&
              p.product_id !== currentProd.product_id,
          );
          setRelatedProducts(
            filtered.length > 0 ? filtered.slice(0, 5) : mapped.slice(0, 5),
          );
        } catch (e) {
          console.error("Failed to load related products:", e);
        }
      }
      setLoading(false);
    };

    loadDetails();
  }, [id]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-32">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-gray-200 border-t-[#CAE4C9]" />
      </div>
    );
  }

  if (!product) {
    return (
      <div className="flex items-center justify-center py-32 text-sm text-gray-400">
        Product not found.
      </div>
    );
  }

  const images = product.images ?? [];

  return (
    <div>
      {/* Breadcrumb */}
      <div
        className="w-full h-32 bg-cover bg-center relative flex items-center p-8 border-b border-gray-100 overflow-hidden"
        style={{ backgroundImage: "url(/img/bg/banner18.jpg)" }}
      >
        <nav className="text-xs text-gray-400 font-medium tracking-wide justify-center items-center m-auto text-center">
          <h2 className="text-2xl text-black font-semibold mb-2">
            Product Details
          </h2>
          <Link to="/products" className="hover:text-[#43a047] transition-colors">
            Products
          </Link>
          <span className="mx-2 text-gray-300">/</span>
          <span className="text-gray-600">{product.name}</span>
        </nav>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-14">
          {/* Images */}
          <div>
            {/* Main image with zoom on hover */}
            <div className="border border-border-color mb-4 bg-gray-50 overflow-hidden cursor-zoom-in">
              {mainImg ? (
                <ImageWithSkeleton
                  src={mainImg}
                  alt={product.name}
                  containerClassName="w-full h-96"
                  className="w-full h-96 object-contain transition-transform duration-500 ease-in-out hover:scale-125"
                  draggable={false}
                />
              ) : (
                <div className="w-full h-96 flex items-center justify-center text-gray-300 text-sm">
                  No image available
                </div>
              )}
            </div>

            {/* Thumbnails */}
            {images.length > 0 && (
              <div className="flex gap-2 flex-wrap">
                {images.map((img) => {
                  const imgUrl = img.image.startsWith("http")
                    ? img.image
                    : `${IMG_BASE}/${img.image}`;
                  return (
                    <button
                      key={img.product_image_id}
                      onClick={() => setMainImg(imgUrl)}
                      className={`w-20 h-20 border-2 overflow-hidden ${
                        mainImg === imgUrl
                          ? "border-primary"
                          : "border-border-color"
                      }`}
                    >
                      <ImageWithSkeleton
                        src={imgUrl}
                        alt=""
                        containerClassName="w-full h-full"
                        className="w-full h-full object-cover transition-transform duration-500 ease-in-out hover:scale-125"
                      />
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Details */}
          <div>
            <h1 className="text-2xl font-bold text-dark mb-2">
              {product.name}
            </h1>
            <div className="flex items-center gap-2 mb-3">
              {[1, 2, 3, 4, 5].map((s) => (
                <FaStar key={s} className="text-yellow-400" size={14} />
              ))}
              <span className="text-xs text-gray-400">(23 reviews)</span>
            </div>

            {/* Description */}
            {product.description && (
              <div
                className="raw-html-content prose prose-sm max-w-none text-dark leading-relaxed mb-5"
                dangerouslySetInnerHTML={{ __html: product.description }}
              />
            )}

            {/* Actions */}
            <div className="flex items-center gap-3 mb-6 mt-4">
              <button
                onClick={() => openInquiry(product)}
                className="btn-readmore hover:text-black flex items-center gap-2 px-6 py-3 text-xs font-bold uppercase tracking-wider rounded-sm shadow-md transition-all duration-300"
              >
                Send Enquiry 
              </button>
            </div>
 

            <div className="border-t border-border-color pt-4 space-y-2 text-sm text-gray-500">
              {product.category && (
                <p>
                  <span className="font-medium text-dark">Category:</span>{" "}
                  {product.category.name}
                </p>
              )}
              <p>
                <span className="font-medium text-dark">Status:</span>{" "}
                <span
                  className={
                    product.status === "active"
                      ? "text-green-500"
                      : "text-red-400"
                  }
                >
                  {product.status === "active" ? "In Stock" : "Unavailable"}
                </span>
              </p>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="mb-14">
          <div className="flex border-b border-border-color mb-6">
            {[
              { key: "additional", label: "Additional Information" },
              { key: "reviews", label: "Reviews" },
            ].map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`px-5 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${
                  activeTab === tab.key
                    ? "border-primary text-primary"
                    : "border-transparent text-gray-500 hover:text-dark"
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          <div className="text-sm text-gray-500 leading-relaxed">
            {activeTab === "additional" &&
              (product.additional_information ? (
                <div
                  className="raw-html-content prose prose-sm max-w-none text-dark leading-relaxed"
                  dangerouslySetInnerHTML={{
                    __html: product.additional_information,
                  }}
                />
              ) : (
                <p>No additional information available.</p>
              ))}

            {activeTab === "reviews" && (
              <div>
                <p className="mb-4 font-medium text-dark">
                  23 reviews for {product.name}
                </p>
                {[
                  {
                    name: "Alice",
                    text: "Great product! Very fresh and delivered quickly.",
                    rating: 5,
                  },
                  {
                    name: "Bob",
                    text: "Good quality organic product. Will order again.",
                    rating: 4,
                  },
                ].map((r, i) => (
                  <div
                    key={i}
                    className="mb-4 pb-4 border-b border-border-color"
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-medium text-dark text-sm">
                        {r.name}
                      </span>
                      <div className="flex">
                        {[1, 2, 3, 4, 5].map((s) => (
                          <FaStar
                            key={s}
                            className={
                              s <= r.rating
                                ? "text-yellow-400"
                                : "text-gray-200"
                            }
                            size={11}
                          />
                        ))}
                      </div>
                    </div>
                    <p className="text-xs">{r.text}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Related */}
        <div>
          <div className="section-title mb-6">
            <p>You may also like</p>
            <h2>Related Products</h2>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
            {relatedProducts.map((p) => (
              <ProductCard key={p.id} product={p} />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}